import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, LogOut, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const SS_KEY = 'px_price_list_data';

export default function PriceList() {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    const cached = sessionStorage.getItem(SS_KEY);
    if (cached) {
      try {
        setGroups(JSON.parse(cached));
      } catch (e) {
        sessionStorage.removeItem(SS_KEY);
      }
    }
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/site/price-list`, { password });
      setGroups(data.groups);
      sessionStorage.setItem(SS_KEY, JSON.stringify(data.groups));
      setPassword('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Password salah');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(SS_KEY);
    setGroups(null);
  };

  return (
    <div data-testid="page-price-list" className="pt-[68px] md:pt-[100px] bg-white min-h-screen">
      {/* Hero */}
      <section className="relative bg-neutral-950 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(239,68,68,0.35), transparent 50%)',
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 py-16 md:py-24 text-white">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
          </Link>
          <p className="text-[11px] tracking-[0.3em] text-red-400 font-semibold mb-3">
            AKSES DATA · UNTUK DEALER & RESELLER
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
            Daftar Harga PowerX &amp; Growatt
          </h1>
          <p className="text-neutral-300 mt-4 max-w-2xl">
            Estimasi harga jual dealer untuk seluruh lini inverter, baterai
            LiFePO4, dan portable power station.
          </p>
        </div>
      </section>

      {/* Gate */}
      {!groups ? (
        <section className="max-w-md mx-auto px-6 py-20">
          <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-px-red text-white p-3 rounded-xl">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-neutral-900">
                  Halaman terkunci
                </h2>
                <p className="text-sm text-neutral-500">
                  Masukkan password dealer untuk melanjutkan
                </p>
              </div>
            </div>
            <form onSubmit={unlock} className="space-y-4">
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoFocus
                  data-testid="price-list-password"
                  className="w-full bg-white border border-neutral-300 rounded-full px-5 py-3 pr-12 outline-none focus:border-px-red focus:ring-2 focus:ring-red-200 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                  aria-label="toggle password"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-px-red" data-testid="price-list-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="price-list-unlock"
                className="w-full bg-px-red hover:bg-red-700 text-white rounded-full py-6 text-sm font-semibold"
              >
                {loading ? 'Memeriksa…' : 'Buka Daftar Harga'}
              </Button>
            </form>
            <p className="text-xs text-neutral-400 mt-5 leading-relaxed">
              Belum punya akses dealer? Hubungi tim penjualan PowerX melalui
              halaman <Link to="/contact" className="underline">Kontak</Link>.
            </p>
          </div>
        </section>
      ) : (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-12 py-14">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-10">
            <p className="text-sm text-neutral-500">
              Harga tanggal akses · estimasi jual · dapat berubah sewaktu-waktu
              tanpa pemberitahuan.
            </p>
            <button
              onClick={logout}
              data-testid="price-list-logout"
              className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 border border-neutral-300 rounded-full px-4 py-2"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>

          <div className="space-y-14">
            {groups.map((g, gi) => (
              <div key={gi} data-testid={`price-group-${gi}`}>
                <div className="mb-5">
                  <p className="text-[11px] tracking-[0.25em] text-px-red font-bold">
                    {g.subtitle}
                  </p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 mt-1">
                    {g.title}
                  </h2>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-neutral-200">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-900 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold w-20">Foto</th>
                        {g.columns.map((c, ci) => (
                          <th
                            key={ci}
                            className={`px-4 py-3 text-left font-semibold whitespace-nowrap ${
                              ci === g.columns.length - 1 ? 'text-right' : ''
                            } ${ci === 0 ? 'w-14' : ''}`}
                          >
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((row, ri) => (
                        <tr
                          key={ri}
                          className={ri % 2 ? 'bg-neutral-50' : 'bg-white'}
                        >
                          <td className="px-4 py-2 align-middle">
                            {g.images && g.images[String(row[0])] ? (
                              <img
                                src={g.images[String(row[0])]}
                                alt={row[1]}
                                loading="lazy"
                                className="w-14 h-14 object-contain rounded-lg border border-neutral-200 bg-white"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-lg border border-dashed border-neutral-200 bg-neutral-50" />
                            )}
                          </td>
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              className={`px-4 py-3 align-middle ${
                                ci === row.length - 1
                                  ? 'text-right font-semibold text-neutral-900 whitespace-nowrap'
                                  : 'text-neutral-700'
                              } ${ci === 0 ? 'text-neutral-500' : ''}`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 p-6 bg-neutral-50 border border-neutral-200 rounded-2xl text-sm text-neutral-600 leading-relaxed">
            <strong className="text-neutral-900">Catatan:</strong> Harga di atas
            adalah estimasi harga jual untuk referensi internal dealer. Harga
            final mengikuti penawaran resmi dari tim PowerX dan dapat berubah
            tergantung quantity, lokasi pengiriman, dan masa promo. Untuk
            penawaran proyek silakan hubungi <Link to="/contact" className="text-px-red underline">tim penjualan</Link>.
          </div>
        </section>
      )}
    </div>
  );
}
